import { useMemo } from 'react';
import { useFetchAccounts } from './accounts/useAccounts.ts';
import { useExchangeRates } from './useExchangeRates';
import type { PortfolioMetrics, AccountWithConvertedBalance } from '@/types/account-drilldown';

/**
 * Hook to calculate portfolio-level aggregated metrics
 * Combines account data with exchange rates to compute total net worth
 * and group accounts by type
 * 
 * @param baseCurrency - The user's preferred currency for aggregated values
 * @returns Portfolio metrics with loading state and error
 */
export function usePortfolioMetrics(baseCurrency: string = 'USD') {
  // Fetch all accounts
  const { data: accounts, isLoading: accountsLoading, error: accountsError } = useFetchAccounts();
  
  // Fetch exchange rates
  const { 
    rates, 
    isLoading: ratesLoading, 
    error: ratesError,
    convert,
    isSupported 
  } = useExchangeRates(baseCurrency);

  // Calculate portfolio metrics
  const metrics = useMemo((): PortfolioMetrics | undefined => {
    if (!accounts || !rates) return undefined;

    let totalNetWorth = 0;
    const accountsWithConvertedBalances: AccountWithConvertedBalance[] = [];
    const accountsByType: Record<string, AccountWithConvertedBalance[]> = {};

    // Process each account
    for (const account of accounts) {
      // Skip inactive accounts
      if (account.status !== 'active') continue;

      let convertedBalance = account.balance;
      let conversionRate = 1;

      // Convert balance to base currency if needed
      if (account.currency !== baseCurrency) {
        // Check if currency is supported
        if (!isSupported(account.currency)) {
          console.warn(
            `Exchange rate not available for ${account.currency}. ` +
            `Account "${account.name}" excluded from portfolio calculations.`
          );
          continue;
        }

        try {
          convertedBalance = convert(account.balance, account.currency, baseCurrency);
          const rate = rates.rates[account.currency];
          const baseRate = rates.rates[baseCurrency];
          conversionRate = baseRate / rate;
        } catch (error) {
          console.error(
            `Error converting ${account.currency} to ${baseCurrency} for account "${account.name}":`,
            error
          );
          continue;
        }
      }

      // Add to total net worth
      totalNetWorth += convertedBalance;

      // Add to converted balances array
      accountsWithConvertedBalances.push({
        ...account,
        convertedBalance,
        conversionRate,
      });

      // Group by type
      const acctWithBalance: AccountWithConvertedBalance = { ...account, convertedBalance, conversionRate };
      if (!accountsByType[account.type]) {
        accountsByType[account.type] = [];
      }
      accountsByType[account.type].push(acctWithBalance);
    }

    return {
      totalNetWorth,
      accountsByType,
      accountsWithConvertedBalances,
    };
  }, [accounts, rates, baseCurrency, convert, isSupported]);

  // Combine loading and error states
  const isLoading = accountsLoading || ratesLoading;
  const error = accountsError || ratesError;

  return {
    metrics,
    isLoading,
    error: error as Error | null,
  };
}
